// @ts-nocheck
import {Vector2} from 'three';
import type {ColorRepresentation} from 'three';
import * as THREE from 'three/webgpu';
import {
    attribute,
    cameraProjectionMatrix,
    cameraViewMatrix,
    Fn,
    modelWorldMatrix,
    vec2,
    vec4,
    viewportSize,
} from 'three/tsl';


/** 屏幕空间实线像素偏移 attribute 名称。 */
export const SCREEN_SPACE_SOLID_LINE_OFFSET_ATTRIBUTE_NAME = 'screenSpaceLineOffset';

/** 屏幕空间实线材质默认值来源。 */
const DEFAULT_VALUES = new THREE.LineBasicMaterial();

/**
 * 屏幕空间实线顶点像素偏移值。
 */
export type ScreenSpaceSolidLineOffsetValue = Vector2 | readonly [number, number];

/**
 * 屏幕空间实线材质参数。
 */
export interface ScreenSpaceSolidLineNodeMaterialParameters extends THREE.NodeMaterialParameters {
    /** 材质颜色。 */
    color?: ColorRepresentation;
    /** 线宽。 */
    linewidth?: number;
    /** 材质透明度。 */
    opacity?: number;
    /** 是否透明。 */
    transparent?: boolean;
    /** 是否启用深度测试。 */
    depthTest?: boolean;
    /** 是否写入深度缓冲。 */
    depthWrite?: boolean;
}

/**
 * 归一化屏幕空间实线像素偏移。
 *
 * @param offset 输入像素偏移。
 * @returns 二维像素偏移。
 */
function normalizeScreenSpaceSolidLineOffset(offset: ScreenSpaceSolidLineOffsetValue): Vector2 {
    if (offset instanceof Vector2) {
        return offset;
    }

    return new Vector2(offset[0], offset[1]);
}

/**
 * 屏幕空间实线 Node 材质。
 *
 * 设计说明：
 * 1. 几何体 `position` 只作为 three.js 必需顶点入口，真实顶点位置由本材质在裁剪空间计算；
 * 2. 对象世界原点作为锚点，`screenSpaceLineOffset` attribute 表达相对锚点的屏幕像素偏移；
 * 3. 偏移在乘回 `clip.w` 后写入裁剪空间，因此相机缩放、拉近或正交缩放都不会改变线段屏幕尺寸。
 */
export class ScreenSpaceSolidLineNodeMaterial extends THREE.NodeMaterial {
    /** 运行时材质标记，用于测试与调试识别。 */
    public readonly isScreenSpaceSolidLineNodeMaterial: boolean = true;

    /**
     * 构造屏幕空间实线 Node 材质。
     *
     * @param parameters 材质参数。
     */
    public constructor(parameters: ScreenSpaceSolidLineNodeMaterialParameters = {}) {
        super();

        this.setDefaultValues(DEFAULT_VALUES);
        this.vertexNode = this.createVertexNode();
        this.setValues(parameters);
    }

    /**
     * 创建裁剪空间顶点节点。
     *
     * @returns 用于 `vertexNode` 的裁剪空间位置节点。
     */
    private createVertexNode(): unknown {
        return Fn(() => {
            const offsetNode = attribute(SCREEN_SPACE_SOLID_LINE_OFFSET_ATTRIBUTE_NAME, 'vec2');
            const anchorWorldNode = modelWorldMatrix.mul(vec4(0, 0, 0, 1));
            const anchorViewNode = cameraViewMatrix.mul(anchorWorldNode);
            const anchorClipNode = cameraProjectionMatrix.mul(anchorViewNode).toVar('screenSpaceSolidLineAnchorClip');

            // offsetNode 使用屏幕像素语义：X 向右为正，Y 向上为正。
            const ndcOffsetNode = vec2(
                offsetNode.x.mul(2).div(viewportSize.x),
                offsetNode.y.mul(2).div(viewportSize.y),
            );
            const clipOffsetNode = ndcOffsetNode.mul(anchorClipNode.w);

            return anchorClipNode.add(vec4(clipOffsetNode, 0, 0));
        })();
    }
}

/**
 * 判断目标值是否为屏幕空间实线 Node 材质。
 *
 * @param value 待判断值。
 * @returns 当目标值为屏幕空间实线 Node 材质时返回 true。
 */
export function isScreenSpaceSolidLineNodeMaterial(value: unknown): value is ScreenSpaceSolidLineNodeMaterial {
    return Boolean(value)
        && typeof value === 'object'
        && Reflect.get(value, 'isScreenSpaceSolidLineNodeMaterial') === true;
}

/**
 * 将屏幕空间像素偏移写入线几何体。
 *
 * @param geometry 待写入的线几何体。
 * @param offsets 每个顶点相对对象世界原点的屏幕像素偏移。
 */
export function setScreenSpaceSolidLineGeometryOffsets(
    geometry: THREE.BufferGeometry,
    offsets: ScreenSpaceSolidLineOffsetValue[],
): void {
    const positions = new Float32Array(offsets.length * 3);
    const screenOffsets = new Float32Array(offsets.length * 2);

    offsets.forEach((offset, index) => {
        const normalizedOffset = normalizeScreenSpaceSolidLineOffset(offset);

        // `position` 保持在对象原点，让实际长度完全由屏幕像素偏移 attribute 控制。
        positions[index * 3] = 0;
        positions[index * 3 + 1] = 0;
        positions[index * 3 + 2] = 0;
        screenOffsets[index * 2] = normalizedOffset.x;
        screenOffsets[index * 2 + 1] = normalizedOffset.y;
    });

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute(
        SCREEN_SPACE_SOLID_LINE_OFFSET_ATTRIBUTE_NAME,
        new THREE.BufferAttribute(screenOffsets, 2),
    );
    geometry.computeBoundingSphere();
}

